import { useCallback, useEffect, useState } from "react";
import type { SessionKind, TeacherSessionSummary } from "@studio/shared";
import { InlineSpinner } from "../components/InlineSpinner";
import { TeacherMessagePage, TeacherModal, TeacherQrOverlay } from "../components/TeacherChrome";
import { SessionCard } from "../components/sessions/SessionCard";
import { navigate } from "../lib/navigation";
import { useBackClosable } from "@teachermate/shared/back";
import {
  TeacherApiError,
  TeacherLoginRequiredError,
  TeacherRetryableError,
  teacherRequest,
} from "../lib/teacherApi";

/** 만들기 버튼의 ＋ — 글자가 아니라 선 아이콘이다(BUILDER-UX §4 D2). */
function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M8 3 L8 13 M3 8 L13 8" />
    </svg>
  );
}

// 학생 1명당 곡 수. 서버 계약(server/src/routes/session.ts)과 DB CHECK(007)는 1~10 을 받는다.
// 🔑 5곡 선택지는 지웠다(2026-10-18 jery) — 늘 10곡이고, 더 필요하면 진행판의 «+5곡» 선물로 준다.
const MUSIC_PER_STUDENT_LIMIT = 10;

interface CreateForm {
  kind: SessionKind;
  title: string;
}

const emptyForm: CreateForm = {
  kind: "music",
  title: "",
};

export function TeacherDashPage() {
  const [sessions, setSessions] = useState<TeacherSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState<"login" | "forbidden" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  // 포스터 수업 스위치(MVS_POSTER_ENABLED) — 꺼져 있으면 「수업 종류」에 포스터가 없다.
  const [posterEnabled, setPosterEnabled] = useState(false);
  const [qrSession, setQrSession] = useState<TeacherSessionSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TeacherSessionSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [extendingId, setExtendingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateForm>(emptyForm);

  const load = useCallback(async (): Promise<void> => {
    try {
      const response = await teacherRequest<{
        sessions: TeacherSessionSummary[];
        video_enabled: boolean;
        poster_enabled?: boolean;
      }>("/api/sessions");
      setSessions(response.sessions);
      setVideoEnabled(response.video_enabled);
      setPosterEnabled(response.poster_enabled === true);
      setError(null);
    } catch (caught) {
      if (caught instanceof TeacherLoginRequiredError) setFatal("login");
      else if (caught instanceof TeacherApiError && caught.status === 403) setFatal("forbidden");
      else setError(caught instanceof TeacherRetryableError ? "연결이 불안정해요. 다시 불러와 주세요." : "수업 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = (): void => {
    setTitleError(null);
    setError(null);
    setCreateOpen(true);
  };

  const closeCreate = (): void => {
    if (creating) return;
    setCreateOpen(false);
    setTitleError(null);
  };
  // 🚨 훅은 아래 조건부 return 보다 먼저 부른다 — fatal 화면에서 훅 수가 줄면 React #300 흰 화면(2026-10-05 검수 QA-01).
  // 수업 만들기 창은 만든 뒤 새 수업 화면으로 간다 — 창 칸을 먼저 치운 뒤에(closeThen). 안 그러면 새 화면에서 뒤로가기를 두 번 눌러야 한다.
  const { closeThen: closeCreateThen } = useBackClosable(createOpen, closeCreate);

  if (fatal) return <TeacherMessagePage kind={fatal} />;

  const liveCount = sessions.filter((session) => session.status === "live").length;

  const createSession = async (): Promise<void> => {
    if (!form.title.trim()) {
      setError(null);
      setTitleError("수업 이름을 입력해 주세요.");
      return;
    }
    setTitleError(null);
    setCreating(true);
    setError(null);
    try {
      const created = await teacherRequest<{ id: string; code: string }>("/api/sessions", {
        method: "POST",
        body: JSON.stringify(form.kind === "music"
          ? {
              kind: "music",
              title: form.title.trim(),
              per_student_limit: MUSIC_PER_STUDENT_LIMIT,
              // 허밍은 교사가 고르지 않는다 — 음악 수업이면 항상 켠다(2026-08-09 prod QA).
              humming_enabled: true,
            }
          : {
              // 영상·그림 수업은 한도를 서버가 정한다 — 종류와 이름만 보낸다.
              kind: form.kind,
              title: form.title.trim(),
            }),
      });
      closeCreateThen(() => navigate(`/teacher/live/${encodeURIComponent(created.id)}`));
    } catch (caught) {
      if (caught instanceof TeacherLoginRequiredError) setFatal("login");
      else setError(caught instanceof Error ? caught.message : "수업을 만들지 못했습니다.");
    } finally {
      setCreating(false);
    }
  };

  /**
   * 수업 «완전 삭제»(2026-08-11). 되돌릴 수 없으므로 반드시 확인 모달을 거친다.
   *
   * 🔑 성공하면 목록을 다시 부른다 — 지운 카드만 화면에서 빼면 참여·완성 집계가 옛 숫자로 남는다.
   */
  const deleteSession = async (): Promise<void> => {
    const target = deleteTarget;
    if (!target) return;
    setDeleting(true);
    setError(null);
    try {
      await teacherRequest(`/api/sessions/${encodeURIComponent(target.id)}`, { method: "DELETE" });
      setDeleteTarget(null);
      await load();
    } catch (caught) {
      if (caught instanceof TeacherLoginRequiredError) setFatal("login");
      else setError(caught instanceof Error ? caught.message : "수업을 삭제하지 못했습니다.");
    } finally {
      setDeleting(false);
    }
  };

  /**
   * «1년 더 보관»(보관 규칙 2026-10-01). 서버는 두 번 눌러도 같은 날짜를 준다.
   * 🔑 성공하면 목록을 다시 부른다 — 카드의 날짜를 화면에서 고쳐 쓰면 서버와 어긋날 수 있다.
   */
  const extendRetention = async (target: TeacherSessionSummary): Promise<void> => {
    setExtendingId(target.id);
    setError(null);
    try {
      await teacherRequest(`/api/sessions/${encodeURIComponent(target.id)}/retention/extend`, { method: "POST" });
      await load();
    } catch (caught) {
      if (caught instanceof TeacherLoginRequiredError) setFatal("login");
      else setError(caught instanceof Error ? caught.message : "보관 기간을 늘리지 못했습니다.");
    } finally {
      setExtendingId(null);
    }
  };

  return (
    <main className="teacher-page">
      <div className="teacher-page-inner teacher-dashboard">
        {/* §4 헤더: 왼쪽 제목 블록(제목 + 「N개 수업 · 진행 중 M개」) + 오른쪽 액션. items-end 정렬. */}
        <header className="teacher-page-heading teacher-dash-heading" aria-label="내 수업 헤더">
          <div>
            <h1>내 수업</h1>
            <p>{sessions.length}개 수업 · 진행 중 {liveCount}개</p>
          </div>
          <div className="teacher-dash-header-actions">
            <button className="button-ghost" type="button" onClick={() => void load()} disabled={loading}>새로고침</button>
            {/* 시연작 진입구는 여기가 아니라 «수업 현황 상세» 다 — DESIGN-POLICY §9.H-14 v1.10. */}
            {/* ＋ 는 글자가 아니라 선 아이콘이다(D2) — 글자 ＋ 는 버튼 안에서 아이콘으로 읽히지 않는다. */}
            <button className="button-primary teacher-dash-create" type="button" onClick={openCreate}>
              <PlusIcon />
              수업 만들기
            </button>
          </div>
        </header>

        {error ? <div className="teacher-error-banner" role="alert">{error}</div> : null}

        <section className="teacher-session-section" aria-label="수업 목록">
          {loading ? <p className="teacher-empty">수업을 불러오는 중...</p> : null}
          {!loading && sessions.length === 0
            ? (
              <div className="teacher-empty-state">
                {/*
                  빈 화면은 «없다»고만 말하지 않는다 — 4세트가 정본이다(DESIGN-POLICY §8, D12):
                  선 아이콘(44px 원형) + 「아직 ~이 없어요」 + 유도 문구 + CTA.
                  🚨 이모지 금지 — 기기마다 크기·모양이 달라 화면이 장난스러워진다.
                  🚨 헤더의 「수업 만들기」 는 이때도 숨기지 않는다(위 header 는 그대로 그려진다).
                */}
                <span className="teacher-empty-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18V5l12-2v13" />
                    <circle cx="6" cy="18" r="3" />
                    <circle cx="18" cy="16" r="3" />
                  </svg>
                </span>
                <p className="teacher-empty-title">아직 만든 수업이 없어요</p>
                <p className="teacher-empty-lead">수업을 만들면 학생들이 참여할 수 있어요</p>
                <button className="button-primary teacher-dash-create" type="button" onClick={openCreate}>
                  <PlusIcon />
                  첫 수업 만들기
                </button>
              </div>
            )
            : null}
          <div className="teacher-session-list">
            {sessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onOpen={() => navigate(`/teacher/live/${encodeURIComponent(session.id)}`)}
                onQr={() => setQrSession(session)}
                onDelete={() => setDeleteTarget(session)}
                onExtendRetention={() => void extendRetention(session)}
                extending={extendingId === session.id}
              />
            ))}
          </div>
        </section>
      </div>

      {/* §4 「수업 생성: 모달」 — 목록 위에 띄워 맥락을 유지한다(예전엔 목록 위에 상시 펼쳐진 폼이었다). */}
      {createOpen ? (
        <TeacherModal
          title="수업 만들기"
          wide
          backClosable={false}
          onClose={closeCreate}
          actions={(
            <>
              <button className="button-secondary" type="button" onClick={closeCreate} disabled={creating}>취소</button>
              <button className="button-primary" type="button" disabled={creating} onClick={() => void createSession()}>
                {creating ? <InlineSpinner label="만드는 중..." /> : "만들기"}
              </button>
            </>
          )}
        >
          <p className="teacher-modal-lead">수업 이름을 입력하면 참여 코드가 자동 발급돼요</p>
          <div className="teacher-form-grid">
            <label>
              <span>수업 종류</span>
              <select value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value as CreateForm["kind"] })}>
                <option value="music">음악 만들기</option>
                <option value="mv">영상 만들기</option>
                <option value="image">그림 만들기</option>
                {posterEnabled ? <option value="poster">포스터 만들기</option> : null}
              </select>
            </label>
            <label className="teacher-form-wide">
              <span>수업 이름</span>
              <input
                value={form.title}
                maxLength={100}
                placeholder="예: 5학년 2반 우리 반 노래"
                aria-invalid={titleError ? true : undefined}
                aria-describedby={titleError ? "teacher-session-title-error" : undefined}
                onChange={(event) => {
                  setForm({ ...form, title: event.target.value });
                  if (titleError) setTitleError(null);
                }}
              />
              {titleError ? <span className="teacher-field-error" id="teacher-session-title-error" role="alert">{titleError}</span> : null}
            </label>
            {form.kind === "music" ? (
              <p className="teacher-video-note">학생 1명이 노래를 {MUSIC_PER_STUDENT_LIMIT}곡까지 만들 수 있어요. 더 필요하면 수업 중에 선물로 늘려 줄 수 있어요.</p>
            ) : form.kind === "image" || form.kind === "poster" ? null : (
              /*
                🚨 «1인 AI 영상 컷 한도»(0·2·3개)·«1인 그림 다시 만들기 한도» 선택기는 **삭제했다**
                   (2026-08-12 jery 결정). 한도 «값»은 없앤 게 아니라 서버가 정한다(session.ts) —
                   영상은 컷 수만큼, 그림은 20회. DB 예약 함수가 이 값으로 막고 있어서
                   (004_mvs_edit_compose.sql:209) 0 으로 두면 영상이 아예 안 만들어진다.

                🗑️ 그 아래 설명 문구 2줄(«컷마다 학생이 …», «그림은 한 사람이 20번까지 …»)도
                   **삭제했다**(2026-08-14 jery QA). 수업을 «만드는» 자리에서 교사가 고를 것이
                   없는 규칙을 읽게 할 이유가 없다 — 정작 필요한 순간(학생이 한도에 걸렸을 때)은
                   라이브 화면의 선물 버튼이 맡는다.
                   ⚠️ 다만 「AI 영상 준비 중」은 문구가 아니라 **상태 경고**라 남긴다 —
                      이걸 지우면 교사가 영상이 켜진 줄 알고 수업을 연다.
              */
              !videoEnabled ? (
                <p className="teacher-video-note">AI 영상은 준비 중이에요. 지금은 카메라 움직임을 무료로 사용할 수 있어요.</p>
              ) : null
            )}
          </div>
          <p className="teacher-modal-foot">
            {form.kind === "music"
              ? "음악 만들기 수업을 시작해요."
              : form.kind === "image"
                ? "그림을 만들고, 원하면 노래를 붙여 작품 카드로 완성해요."
                : form.kind === "poster"
                  ? "AI 그림으로 포스터를 꾸미고, 포스터 QR 로 그 학생 AR 을 열어요. 학생은 PC·크롬북으로 해요."
                  : "곡 선택부터 컷 그림 완성까지 진행해요."}
          </p>
        </TeacherModal>
      ) : null}

      {deleteTarget ? (
        <TeacherModal
          title="수업을 삭제할까요?"
          onClose={() => { if (!deleting) setDeleteTarget(null); }}
          destructive
          actions={(
            <>
              <button className="button-secondary" type="button" data-teacher-modal-cancel disabled={deleting} onClick={() => setDeleteTarget(null)}>그대로 두기</button>
              <button className="button-danger" type="button" disabled={deleting} onClick={() => void deleteSession()}>
                {deleting ? <InlineSpinner label="삭제 중..." /> : "삭제"}
              </button>
            </>
          )}
        >
          {/* 🚨 무엇이 사라지는지 적는다. 「정말 삭제할까요?」만 묻는 모달은 각오할 거리를 주지 않는다. */}
          <p><b>{deleteTarget.title}</b> 수업을 완전히 지워요. 되돌릴 수 없어요.</p>
          <p>{deleteTarget.kind === "mv"
            ? "참여 학생 명단, 만든 컷 그림과 올린 음악, 완성한 영상까지 모두 사라져요."
            : deleteTarget.kind === "poster"
              ? "참여 학생 명단, 만든 그림·포스터·올린 사진, 포스터 AR 까지 모두 사라져요."
              : deleteTarget.kind === "image"
              ? "참여 학생 명단, 만든 그림과 붙인 노래까지 모두 사라져요. 학교 전시 페이지에서도 빠져요."
              : "참여 학생 명단, 허밍 녹음, 완성한 노래까지 모두 사라져요."
          }</p>
        </TeacherModal>
      ) : null}

      {qrSession ? (
        <TeacherQrOverlay
          title={qrSession.title}
          code={qrSession.code}
          onClose={() => setQrSession(null)}
        />
      ) : null}

    </main>
  );
}
